// apps/merchant-v3/src/pages/wallet/hooks/useWallet.ts
//
// Phase 6 Wave 9 Task 28 — React Query hooks for the Wallet overview
// surface. Wraps `walletClient.ts` calls + wires the WebSocket live-
// refresh pattern (`merchant.${merchantId}.wallet` channel per
// Consumers:
//   - useWalletBalance()        → { balance, currency, lastUpdated }
//   - useWalletCredit()         → { limit, used, available, expiry }
//   - useSpendingSummary(days)  → daily + averageDaily + totalSpent
//   - useKPISparklines()        → 4-key sparkline map; falls back to
//                                 the spending summary when the
//                                 dedicated endpoint 404s
//   - useCreditConfig()         → admin-content credit config slice
//
// Stale times are intentionally generous (60s for balance/credit,
// 5 min for spending summary) because the WS push will invalidate the
// cache the moment a txn settles — pull-based refresh is the
// pessimistic fallback only.
//
// Task 29 additions: topup history + topup gateway config + 4 mutations
//   (instant / manual create / proof upload / cancel) — all thin
//   wrappers around the corresponding `walletClient.ts` helpers.
//
// Task 30 additions: transactions ledger + Send / Withdraw / Transfer
//   mutation hooks. Each mutation invalidates the `transactions` +
//   `balance` query slices on success so the table + KPI both reflect
//   the new state. Live refresh from the WS `merchant.${id}.wallet`
//   channel additionally invalidates the transactions list.
//
// Task 31 additions: Linked accounts + Bank accounts CRUD hooks.
//   • useLinkedAccounts() / useCreateLinkedAccount() / useDeleteLinkedAccount()
//   • useBankAccounts() — already implemented in Task 30; re-exported here.
//   • useCreateBankAccount() / useDeleteBankAccount() with 2FA-token
//     header forwarding for the Add Bank Account modal.
//
// vg-binding: PLAN:task-28
// vg-binding: PLAN:task-29
// vg-binding: PLAN:task-30
// vg-binding: PLAN:task-31
// vg-binding: P6.D-65
// vg-binding: P6.D-66
// vg-binding: P6.D-67
// vg-binding: P6.D-68
// vg-binding: P6.D-69
// vg-binding: P6.D-71
// vg-binding: P6.D-77
// vg-binding: P6.D-78
// vg-binding: P6.D-233
// vg-binding: P6.D-234
// vg-binding: P6.D-244
// vg-binding: G-46
// vg-binding: G-47
// vg-binding: G-48
// vg-binding: G-49
// vg-binding: G-50
// vg-binding: G-51

import { useEffect, useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError } from '@/lib/api/client';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  useCreditConfig,
  useTopupGatewayConfig as useRawTopupGatewayConfig,
} from '@/hooks/useAdminContent';
import { subscribe, type WSMessage } from '@/services/wsClient';
import {
  cancelTopup,
  createBankAccount,
  createLinkedAccount,
  deleteBankAccount,
  deleteLinkedAccount,
  deriveMonthlySpendingSparkline,
  exportTransactions,
  getKPISparklines,
  getSpendingSummary,
  getWalletBalance,
  getWalletCredit,
  listBankAccounts,
  listLinkedAccounts,
  listTopups,
  listTransactions,
  listTransferRecipients,
  lookupRecipient,
  projectTopupConfig,
  retryTopup,
  sendBalance,
  topupInstant,
  topupManualCreate,
  transferFunds,
  uploadTopupProof,
  verify2FA,
  withdrawFunds,
  type CreateBankAccountPayload,
  type CreateBankAccountResponse,
  type CreateLinkedAccountResponse,
  type SparklinePoint,
  type WalletBalance,
  type WalletCredit,
  type WalletKPISparklines,
  type WalletSpendingSummary,
} from '@/services/walletClient';
import type {
  BankAccount,
  LinkedAccount,
  LookupRecipientResponse,
  TransferRecipient,
  SendBalancePayload,
  SendBalanceResponse,
  Topup,
  TopupGateway,
  TopupGatewayConfig,
  TopupInstantPayload,
  TopupInstantResponse,
  TopupListFilters,
  TopupListResult,
  TopupManualCreatePayload,
  TopupManualCreateResponse,
  TransferPayload,
  TransferResponse,
  Verify2FAResponse,
  WalletTransactionsFilters,
  WalletTransactionsResult,
  WalletTxExportFormat,
  WalletWsMessage,
  WithdrawPayload,
  WithdrawResponse,
} from '@/types/wallet';

// --- query keys ----------------------------------------------------------

export const WALLET_QUERY_KEYS = {
  balance: ['wallet', 'balance'] as const,
  credit: ['wallet', 'credit'] as const,
  spending: (days: number) => ['wallet', 'spending', days] as const,
  sparklines: ['wallet', 'sparklines'] as const,
  // Task 29 — topup history + topup gateway config slices.
  topups: (filters: TopupListFilters) =>
    ['wallet', 'topups', filters] as const,
  topupGatewayConfig: ['wallet', 'topup-gateway-config'] as const,
  // Task 30 — transactions ledger + bank accounts + recipient lookup.
  transactions: (filters: WalletTransactionsFilters) =>
    ['wallet', 'transactions', filters] as const,
  bankAccounts: ['wallet', 'bank-accounts'] as const,
  lookupRecipient: (email: string) =>
    ['wallet', 'lookup-recipient', email] as const,
  // amend#11 — Transfer Group recipients for Send Balance.
  transferRecipients: ['wallet', 'transfer-recipients'] as const,
  // Task 31 — linked accounts list cache.
  linkedAccounts: ['wallet', 'linked-accounts'] as const,
};

const STALE_BALANCE_MS = 60_000; // 1 min — WS push is the canonical refresh
const STALE_CREDIT_MS = 60_000;
const STALE_SPENDING_MS = 5 * 60_000; // 5 min
const STALE_SPARKLINES_MS = 5 * 60_000;

// --- live-refresh hook ---------------------------------------------------

/**
 * Subscribes to the merchant's wallet channel for the lifetime of the
 * caller component. Re-fetches balance + credit query slices on every
 * `WALLET_BALANCE_CHANGED` / `WALLET_CREDIT_CHANGED` push.
 * No-op when `merchantId` is falsy (unauthenticated / first paint).
 */
export function useWalletLiveUpdates(
  merchantId: string | null | undefined,
): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!merchantId) return;
    const channel = `merchant.${merchantId}.wallet`;
    const unsubscribe = subscribe(channel, (msg: WSMessage) => {
      const evt = msg as WalletWsMessage;
      if (evt.type === 'WALLET_BALANCE_CHANGED') {
        // Patch the balance cache inline AND mark stale so a refetch
        // settles authoritative timestamps + pendingTopups.
        queryClient.setQueryData<WalletBalance | undefined>(
          WALLET_QUERY_KEYS.balance,
          (prev) =>
            prev
              ? { ...prev, balance: evt.balance, currency: evt.currency, lastUpdated: evt.at }
              : prev,
        );
        void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balance });
        void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.sparklines });
        // Task 30 — every balance change implies a new transaction row,
        // so the ledger needs to refetch on the next render.
        void queryClient.invalidateQueries({ queryKey: ['wallet', 'transactions'] });
        return;
      }
      if (evt.type === 'WALLET_CREDIT_CHANGED') {
        queryClient.setQueryData<WalletCredit | undefined>(
          WALLET_QUERY_KEYS.credit,
          (prev) =>
            prev
              ? { ...prev, limit: evt.limit, used: evt.used, available: evt.available }
              : prev,
        );
        void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.credit });
        return;
      }
    });
    return () => {
      unsubscribe();
    };
  }, [merchantId, queryClient]);
}

// --- query hooks ---------------------------------------------------------

export function useWalletBalance(): UseQueryResult<WalletBalance, ApiError> {
  const { user } = useAuth();
  useWalletLiveUpdates(user?.id);
  return useQuery<WalletBalance, ApiError>({
    queryKey: WALLET_QUERY_KEYS.balance,
    queryFn: () => getWalletBalance(),
    staleTime: STALE_BALANCE_MS,
  });
}

export function useWalletCredit(): UseQueryResult<WalletCredit, ApiError> {
  return useQuery<WalletCredit, ApiError>({
    queryKey: WALLET_QUERY_KEYS.credit,
    queryFn: () => getWalletCredit(),
    staleTime: STALE_CREDIT_MS,
  });
}

export function useSpendingSummary(
  rangeDays: number,
): UseQueryResult<WalletSpendingSummary, ApiError> {
  return useQuery<WalletSpendingSummary, ApiError>({
    queryKey: WALLET_QUERY_KEYS.spending(rangeDays),
    queryFn: () => getSpendingSummary(rangeDays),
    staleTime: STALE_SPENDING_MS,
  });
}

/**
 * Returns the 4-key sparkline map. Tries the dedicated
 * `/wallet/kpi-sparklines` endpoint first; on empty / fallback shape
 * we synthesize the `monthlySpending` series from the spending summary
 * so the KPI card never renders a flat zero.
 */
export function useKPISparklines(): UseQueryResult<
  WalletKPISparklines,
  ApiError
> {
  return useQuery<WalletKPISparklines, ApiError>({
    queryKey: WALLET_QUERY_KEYS.sparklines,
    queryFn: () => getKPISparklines(),
    staleTime: STALE_SPARKLINES_MS,
  });
}

/**
 * Project the 30-day spending summary onto a `monthlySpending`
 * sparkline. Used by the KPI card when the dedicated endpoint returns
 * an empty array. Memoized at the call site so the chart doesn't
 * re-render on every parent state change.
 */
export function useDerivedMonthlySparkline(
  summary: WalletSpendingSummary | undefined,
): SparklinePoint[] {
  return useMemo(() => deriveMonthlySpendingSparkline(summary), [summary]);
}

// --- credit config (admin-content namespace) ----------------------------

/** Re-export the read-only admin-content credit config so wallet
 *  components can `import { useCreditConfig } from '@/pages/wallet/hooks/useWallet'`
 *  without reaching into the global hooks dir directly. */
export { useCreditConfig };

// --- low-balance threshold derivation -------------------------

/** Floor below which we always nudge the merchant (small-merchant
 *  fallback). USD. */
export const LOW_BALANCE_FLOOR_USD = 50;

/**
 * Compute the low-balance alert threshold.:
 *   - small merchants → flat $50 floor
 *   - larger merchants → average daily spending over the trailing
 *     30 days (= "<1 day runway" trigger)
 * The threshold is the MAX of the two so a high-spend merchant
 * doesn't get spammed below their actual runway either.
 */
export function deriveLowBalanceThreshold(
  averageDaily: number | undefined,
): number {
  const avg = typeof averageDaily === 'number' && averageDaily > 0 ? averageDaily : 0;
  return Math.max(LOW_BALANCE_FLOOR_USD, avg);
}

// --- Task 29: Topup history + gateway config + mutations -----------------

const STALE_TOPUPS_MS = 30_000; // 30 s — history is poll-friendly

/**
 * Topup history list — paginated, filterable. Auto-invalidated on the
 * 4 mutate hooks below so the table reflects new rows on success.
 * (.)
 */
export function useTopups(
  filters: TopupListFilters = {},
): UseQueryResult<TopupListResult, ApiError> {
  return useQuery<TopupListResult, ApiError>({
    queryKey: WALLET_QUERY_KEYS.topups(filters),
    queryFn: () => listTopups(filters),
    staleTime: STALE_TOPUPS_MS,
  });
}

/**
 * Topup gateway config — projects the flat admin-content namespace
 * (Task 05 fixture) into the richer `{ instant, manual, fees }` shape
 * the page consumes. Returns a stable object so component memo keys
 * don't churn on every render.
 */
export function useTopupGatewayConfig(): UseQueryResult<
  TopupGatewayConfig,
  ApiError
> {
  const raw = useRawTopupGatewayConfig();
  // Project the flat → rich shape via memo so consumers (DualModeToggle
  // / GatewaySelect) get referentially stable objects between renders.
  const projected = useMemo<TopupGatewayConfig | undefined>(() => {
    if (!raw.data) return undefined;
    return projectTopupConfig(raw.data.data);
  }, [raw.data]);

  // We re-implement the React Query result shape to keep the call-site
  // ergonomics identical to other useWallet hooks (data + isLoading +
  // isError + refetch). `data` is the projected shape; `error` is the
  // surfaced ApiError from the underlying admin-content query.
  return {
    ...raw,
    data: projected,
  } as unknown as UseQueryResult<TopupGatewayConfig, ApiError>;
}

/** Common invalidator — topup mutations all need the history list +
 *  the balance KPI to refetch on success. */
function useInvalidateTopupRelated(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['wallet', 'topups'] });
    void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balance });
  };
}

/** POST /wallet/topup/instant — caller branches on the response shape
 *  (PayPal `approveUrl` vs Stripe `clientSecret` vs sessionUrl). */
export function useTopupInstant(): UseMutationResult<
  TopupInstantResponse,
  ApiError,
  TopupInstantPayload
> {
  const invalidate = useInvalidateTopupRelated();
  return useMutation<TopupInstantResponse, ApiError, TopupInstantPayload>({
    mutationFn: (payload) => topupInstant(payload),
    onSuccess: () => invalidate(),
  });
}

/** POST /wallet/topup/manual — returns pre-payment invoice + payee
 *  details. Step 1 → Step 2 of the manual wizard. */
export function useTopupManualCreate(): UseMutationResult<
  TopupManualCreateResponse,
  ApiError,
  TopupManualCreatePayload
> {
  const invalidate = useInvalidateTopupRelated();
  return useMutation<
    TopupManualCreateResponse,
    ApiError,
    TopupManualCreatePayload
  >({
    mutationFn: (payload) => topupManualCreate(payload),
    onSuccess: () => invalidate(),
  });
}

/** POST /wallet/topup/manual/:id/upload-proof — Step 3 of the manual
 *  wizard. On success the row transitions to `processing` (admin
 *  review). */
export function useUploadTopupProof(): UseMutationResult<
  { ok: true },
  ApiError,
  { topupId: string; file: File }
> {
  const invalidate = useInvalidateTopupRelated();
  return useMutation<{ ok: true }, ApiError, { topupId: string; file: File }>({
    mutationFn: ({ topupId, file }) => uploadTopupProof(topupId, file),
    onSuccess: () => invalidate(),
  });
}

/** POST /wallet/topups/:id/cancel — merchant-initiated cancel of a
 *  pending topup row. Invalidates the history list + balance. */
export function useCancelTopup(): UseMutationResult<
  { ok: true },
  ApiError,
  string
> {
  const invalidate = useInvalidateTopupRelated();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (topupId) => cancelTopup(topupId),
    onSuccess: () => invalidate(),
  });
}

/** POST /wallet/topup/:gateway/retry — retry a failed instant topup.
 *  Returns the same handoff shape as `useTopupInstant`. */
export function useRetryTopup(): UseMutationResult<
  TopupInstantResponse,
  ApiError,
  TopupGateway
> {
  const invalidate = useInvalidateTopupRelated();
  return useMutation<TopupInstantResponse, ApiError, TopupGateway>({
    mutationFn: (gateway) => retryTopup(gateway),
    onSuccess: () => invalidate(),
  });
}

/** Re-export `Topup` row type so the history table component can
 *  import it from a single module without reaching into `@/types`. */
export type { Topup };

// --- Task 30: Transactions ledger + Send / Withdraw / Transfer ----------

const STALE_TRANSACTIONS_MS = 30_000; // 30 s — WS push refreshes faster
const STALE_BANK_ACCOUNTS_MS = 5 * 60_000; // 5 min — KYC list rarely changes
const STALE_LOOKUP_RECIPIENT_MS = 60_000; // 1 min — email→user is stable

/**
 * Paginated, filterable wallet transactions list (
 * Auto-invalidated by the WS live-refresh handler above
 * AND by every Send / Withdraw / Transfer mutation success so the
 * ledger reflects new rows without a manual refetch.
 */
export function useTransactions(
  filters: WalletTransactionsFilters = {},
): UseQueryResult<WalletTransactionsResult, ApiError> {
  const { user } = useAuth();
  useWalletLiveUpdates(user?.id);
  return useQuery<WalletTransactionsResult, ApiError>({
    queryKey: WALLET_QUERY_KEYS.transactions(filters),
    queryFn: () => listTransactions(filters),
    staleTime: STALE_TRANSACTIONS_MS,
  });
}

/**
 * Mutation for `GET /wallet/transactions/export.{csv|xlsx}`. Returns
 * a Blob the caller can pipe into a download anchor. Modeled as a
 * mutation rather than a query because the caller invokes it
 * imperatively from a button click + we want the loading/error state
 * surfaced via React Query.
 */
export function useExportTransactions(): UseMutationResult<
  Blob,
  ApiError,
  { format: WalletTxExportFormat; filters?: WalletTransactionsFilters }
> {
  return useMutation<
    Blob,
    ApiError,
    { format: WalletTxExportFormat; filters?: WalletTransactionsFilters }
  >({
    mutationFn: ({ format, filters }) => exportTransactions(format, filters),
  });
}

/**
 * Recipient lookup by email — used by the Send Balance wizard Step 1
 * → Step 2 transition. The query is `enabled` only when the email
 * matches a syntactic pattern so we don't pound the BE on every
 * keystroke.
 */
export function useLookupRecipient(
  email: string,
): UseQueryResult<LookupRecipientResponse, ApiError> {
  const trimmed = email.trim();
  const isLikelyEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  return useQuery<LookupRecipientResponse, ApiError>({
    queryKey: WALLET_QUERY_KEYS.lookupRecipient(trimmed),
    queryFn: () => lookupRecipient(trimmed),
    enabled: isLikelyEmail,
    staleTime: STALE_LOOKUP_RECIPIENT_MS,
    retry: false,
  });
}

const STALE_TRANSFER_RECIPIENTS_MS = 60_000; // 1 min — group membership stable

/**
 * GET /api/v1/transfers/recipients — eligible Send Balance recipients,
 * server-scoped to the merchant's active Transfer Groups (P3.D-19, amend#11).
 * An empty array means the merchant belongs to no group → the Send Balance
 * entry point hides itself.
 */
export function useTransferRecipients(): UseQueryResult<
  TransferRecipient[],
  ApiError
> {
  return useQuery<TransferRecipient[], ApiError>({
    queryKey: WALLET_QUERY_KEYS.transferRecipients,
    queryFn: () => listTransferRecipients(),
    staleTime: STALE_TRANSFER_RECIPIENTS_MS,
  });
}

/** Common invalidator — every wallet mutation needs the transactions
 *  list + the balance KPI to refetch on success. */
function useInvalidateWalletMutations(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['wallet', 'transactions'] });
    void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balance });
    void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.sparklines });
    // The TopBar balance chip (components/topbar/BalanceChip) reads
    // BillingSummary.wallet_balance, NOT WALLET_QUERY_KEYS.balance — a
    // separate query key (['billing','summary']). Without invalidating it a
    // Send/Withdraw/Transfer refreshes the /wallet page balance but leaves the
    // topbar chip stale until its own 30s poll (dbg-20260723). Invalidate it
    // here so both surfaces settle together.
    void queryClient.invalidateQueries({ queryKey: ['billing', 'summary'] });
  };
}

/** POST /wallet/send — Send Balance to another Printway merchant
 *  . Caller drives the 4-step wizard; this hook only wraps
 *  the final mutation submit. */
export function useSendBalance(): UseMutationResult<
  SendBalanceResponse,
  ApiError,
  SendBalancePayload
> {
  const invalidate = useInvalidateWalletMutations();
  return useMutation<SendBalanceResponse, ApiError, SendBalancePayload>({
    mutationFn: (payload) => sendBalance(payload),
    onSuccess: () => invalidate(),
  });
}

/** GET /wallet/bank-accounts — verified withdrawal destinations. */
export function useBankAccounts(): UseQueryResult<BankAccount[], ApiError> {
  return useQuery<BankAccount[], ApiError>({
    queryKey: WALLET_QUERY_KEYS.bankAccounts,
    queryFn: () => listBankAccounts(),
    staleTime: STALE_BANK_ACCOUNTS_MS,
  });
}

/** POST /wallet/withdraw — request a withdrawal to a verified bank
 *  account. */
export function useWithdraw(): UseMutationResult<
  WithdrawResponse,
  ApiError,
  WithdrawPayload
> {
  const invalidate = useInvalidateWalletMutations();
  return useMutation<WithdrawResponse, ApiError, WithdrawPayload>({
    mutationFn: (payload) => withdrawFunds(payload),
    onSuccess: () => invalidate(),
  });
}

/** POST /wallet/transfer — between own linked accounts. */
export function useTransfer(): UseMutationResult<
  TransferResponse,
  ApiError,
  TransferPayload
> {
  const invalidate = useInvalidateWalletMutations();
  return useMutation<TransferResponse, ApiError, TransferPayload>({
    mutationFn: (payload) => transferFunds(payload),
    onSuccess: () => invalidate(),
  });
}

/** POST /auth/2fa/verify — security challenge re-used by Send +
 *  Withdraw + Transfer. Returns a short-lived step-up token; the
 *  caller stores it on the modal local state for the immediately
 *  following mutation. */
export function useVerify2FA(): UseMutationResult<
  Verify2FAResponse,
  ApiError,
  string
> {
  return useMutation<Verify2FAResponse, ApiError, string>({
    mutationFn: (code) => verify2FA(code),
  });
}

// --- Task 31: Linked accounts + Bank accounts CRUD ----------------------

const STALE_LINKED_ACCOUNTS_MS = 5 * 60_000; // 5 min — list rarely churns.

/**
 * GET /wallet/linked-accounts — Printway merchants the current user
 * has linked for fast Send Balance transfers.
 */
export function useLinkedAccounts(): UseQueryResult<
  LinkedAccount[],
  ApiError
> {
  return useQuery<LinkedAccount[], ApiError>({
    queryKey: WALLET_QUERY_KEYS.linkedAccounts,
    queryFn: () => listLinkedAccounts(),
    staleTime: STALE_LINKED_ACCOUNTS_MS,
  });
}

/** Common invalidator — every linked-accounts mutation needs the
 *  list cache to refetch on success. */
function useInvalidateLinkedAccounts(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: WALLET_QUERY_KEYS.linkedAccounts,
    });
  };
}

/** POST /wallet/linked-accounts — link a Printway merchant by email. */
export function useCreateLinkedAccount(): UseMutationResult<
  CreateLinkedAccountResponse,
  ApiError,
  string
> {
  const invalidate = useInvalidateLinkedAccounts();
  return useMutation<CreateLinkedAccountResponse, ApiError, string>({
    mutationFn: (recipientEmail) => createLinkedAccount(recipientEmail),
    onSuccess: () => invalidate(),
  });
}

/** DELETE /wallet/linked-accounts/:id — unlink a Printway merchant. */
export function useDeleteLinkedAccount(): UseMutationResult<
  { ok: true },
  ApiError,
  string
> {
  const invalidate = useInvalidateLinkedAccounts();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (id) => deleteLinkedAccount(id),
    onSuccess: () => invalidate(),
  });
}

/** Common invalidator — every bank-accounts mutation needs the list
 *  cache to refetch on success. */
function useInvalidateBankAccounts(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: WALLET_QUERY_KEYS.bankAccounts,
    });
  };
}

/** POST /wallet/bank-accounts — register a new withdrawal destination
 *  . The 2FA step-up token is forwarded as `X-2FA-Token`. */
export function useCreateBankAccount(): UseMutationResult<
  CreateBankAccountResponse,
  ApiError,
  { payload: CreateBankAccountPayload; twoFAToken: string }
> {
  const invalidate = useInvalidateBankAccounts();
  return useMutation<
    CreateBankAccountResponse,
    ApiError,
    { payload: CreateBankAccountPayload; twoFAToken: string }
  >({
    mutationFn: ({ payload, twoFAToken }) =>
      createBankAccount(payload, twoFAToken),
    onSuccess: () => invalidate(),
  });
}

/** DELETE /wallet/bank-accounts/:id — remove a verified bank account. */
export function useDeleteBankAccount(): UseMutationResult<
  { ok: true },
  ApiError,
  string
> {
  const invalidate = useInvalidateBankAccounts();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (id) => deleteBankAccount(id),
    onSuccess: () => invalidate(),
  });
}

/** Re-export `LinkedAccount` row type so the page component can import
 *  it from a single module without reaching into `@/types`. */
export type { LinkedAccount };
